import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Empty, Input, Spin, Tree } from 'antd';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { debounce } from '../../utils/PerformanceUtils';

interface InlineTreeFilterProps {
	treeData: any[];
	value?: Array<string | number>;
	onChange?: (values: Array<string | number>) => void;
	onSearch?: (text: string) => void;
	searchDebounceMs?: number;
	placeholder?: string;
	searchProp?: string;
	loading?: boolean;
	maxHeight?: number;
	width?: number;
	emptyText?: string;
}

const collectExpandableKeys = (nodes: any[]): Array<string | number> => {
	const keys: Array<string | number> = [];
	const walk = (list: any[]) => {
		list.forEach((node) => {
			if (Array.isArray(node.children) && node.children.length > 0) {
				keys.push(node.key);
				walk(node.children);
			}
		});
	};
	walk(nodes);
	return keys;
};

const getSearchableText = (node: any, searchProp: string): string => {
	const fromProp = nullSafeGetOrElse(searchProp, node, '');
	if (typeof fromProp === 'string' && fromProp.length > 0) {
		return fromProp.toLowerCase();
	}
	if (typeof node.title === 'string') {
		return node.title.toLowerCase();
	}
	return '';
};

const filterTree = (
	nodes: any[],
	searchValue: string,
	searchProp: string
): { filtered: any[]; matchingParentKeys: Array<string | number> } => {
	if (!searchValue) {
		return { filtered: nodes, matchingParentKeys: [] };
	}
	const term = searchValue.toLowerCase();
	const matchingParentKeys: Array<string | number> = [];

	const walk = (list: any[]): any[] => {
		const result: any[] = [];
		list.forEach((node) => {
			const text = getSearchableText(node, searchProp);
			const isMatch = text.includes(term);
			const children =
				Array.isArray(node.children) && node.children.length > 0 ? walk(node.children) : [];
			if (isMatch || children.length > 0) {
				result.push({ ...node, children });
				if (children.length > 0) {
					matchingParentKeys.push(node.key);
				}
			}
		});
		return result;
	};

	const filtered = walk(nodes);
	return { filtered, matchingParentKeys };
};

const InlineTreeFilter: React.FC<InlineTreeFilterProps> = ({
	treeData = [],
	value = [],
	onChange,
	onSearch,
	searchDebounceMs = 300,
	placeholder = 'Search',
	searchProp = 'hierarchyName',
	loading = false,
	maxHeight = 360,
	width = 320,
	emptyText = 'No results',
}) => {
	const isServerSearch = typeof onSearch === 'function';
	const [searchValue, setSearchValue] = useState('');
	const [expandedKeys, setExpandedKeys] = useState<Array<string | number>>([]);
	const [autoExpandParent, setAutoExpandParent] = useState(true);

	const debouncedOnSearchRef = useRef<((text: string) => void) | null>(null);
	useEffect(() => {
		if (!onSearch) {
			debouncedOnSearchRef.current = null;
			return;
		}
		debouncedOnSearchRef.current = debounce((text: string) => onSearch(text), searchDebounceMs) as (
			text: string
		) => void;
	}, [onSearch, searchDebounceMs]);

	const { filtered, matchingParentKeys } = useMemo(() => {
		if (isServerSearch) {
			return { filtered: treeData, matchingParentKeys: collectExpandableKeys(treeData) };
		}
		return filterTree(treeData, searchValue, searchProp);
	}, [treeData, searchValue, searchProp, isServerSearch]);

	useEffect(() => {
		if (isServerSearch) {
			setExpandedKeys(collectExpandableKeys(treeData));
		} else if (searchValue) {
			setExpandedKeys(matchingParentKeys);
		} else {
			setExpandedKeys(collectExpandableKeys(treeData));
		}
		setAutoExpandParent(true);
	}, [searchValue, treeData, isServerSearch]);

	const handleExpand = (keys: Array<string | number>) => {
		setExpandedKeys(keys);
		setAutoExpandParent(false);
	};

	const handleCheck = (_checked: any, info: any) => {
		const checkedNodes = nullSafeGetOrElse('checkedNodes', info, []);
		const leafKeys = checkedNodes
			.filter((node: any) => !Array.isArray(node.children) || node.children.length === 0)
			.map((node: any) => node.key);
		if (onChange) {
			onChange(leafKeys);
		}
	};

	const isInitialLoading = loading && treeData.length === 0;

	const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const text = event.target.value;
		setSearchValue(text);
		if (debouncedOnSearchRef.current) {
			debouncedOnSearchRef.current(text);
		}
	};

	return (
		<div className="flex flex-col gap-2" style={{ width }}>
			<Input.Search
				placeholder={placeholder}
				value={searchValue}
				onChange={handleSearchInputChange}
				allowClear
				loading={isServerSearch && loading}
			/>
			<div style={{ maxHeight, overflowY: 'auto' }}>
				{isInitialLoading ? (
					<div className="flex justify-center p-4">
						<Spin />
					</div>
				) : filtered.length === 0 ? (
					<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />
				) : (
					<Tree
						checkable
						selectable={false}
						treeData={filtered}
						checkedKeys={value}
						expandedKeys={expandedKeys}
						autoExpandParent={autoExpandParent}
						onExpand={handleExpand}
						onCheck={handleCheck}
					/>
				)}
			</div>
		</div>
	);
};

export default InlineTreeFilter;
